import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import React from 'react';

import { Header, Footer, SideLine, Background } from '$partials/global/index.js';
import { ShareLinks } from '$partials/editorials';
import { ModuleDispatcher } from '$partials/modules';
import { getBackgroundColours } from '$utilities/site-context';
/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const NewsPage = (props) => {
    const page = props.data.page;

    return (
        <>
            <Background {...getBackgroundColours()} />

            {/* Navigation */}
            <Header navigation={props.data.navigation}/>

            {/* Main content */}
            <main
                className='pt-40 sm:pt-50 md:pt-100 min-h-screen relative'
                id='main-content'>
                <SideLine/>
                <article>
                    <header className='layout-container'>
                        <div className='flex flex-wrap layout-negative-margin'>
                            {/* Heading */}
                            <div className='sm:w-9/12 layout-item-padding'>
                                <h1 className='type-style-2'>
                                    {page?.title}
                                </h1>
                                {page?.subtitle &&
                                <h2 className='type-style-4 font-normal pt-15'>
                                    {page?.subtitle}
                                </h2>
                                }
                            </div>
                        </div>
                    </header>

                    {/* Spacer */}
                    <div className='mt-60'/>

                    <div className='layout-container-padding-right flex flex-wrap max-w-container bg-white pb-40 sm:pb-0 pt-60 sm:pt-70 md:pt-80 rounded-t-md'>
                        {/* Left Sidebar */}
                        <div className='w-sidebar'>
                            <div className='flex flex-col items-center'>
                            </div>
                        </div>

                        {/* Main content */}
                        <div className='relative flex-1 flex flex-wrap self-start layout-negative-margin'>
                            {/* Details */}
                            <div className='sm:w-4/12 sm:absolute z-20 right-0 layout-item-padding'>
                                {(page?.author?.title || page?.customAuthorLabel) &&
                                    <>
                                        <span className='type-style-5'>By </span>
                                        <a href={page?.author?.url}>
                                            <span className='type-style-5'>
                                                {page?.customAuthorLabel || page.author?.title}
                                            </span>
                                        </a>
                                    </>
                                }
                                {page?.publicationDate &&
                                    <p className='type-style-7 pt-5'>
                                        {dayjs(page.publicationDate).format('DD MMM YYYY')}
                                    </p>
                                }
                                {page?.funds?.edges.length > 0 && page?.funds?.edges.map(({node: fund}, i) => {
                                    return (
                                        <div
                                            className={`block md:inline-block md:w-6/12 ${i % 2 === 0 ? 'layout-item-padding-right' : 'md:pl-18'} mt-25 md:mt-30`}
                                            key={i}>
                                            <a
                                                className={`type-style-8 ${fund?.branding?.toLowerCase()}-border background-6px pl-20`}
                                                href={fund.url}
                                                target='_self'>
                                                {fund?.title}
                                            </a>
                                        </div>
                                    );
                                })}
                                <ShareLinks url={props.canonicalUrl}/>
                            </div>
                        </div>
                    </div>

                    {/* Module dispatcher */}
                    {page.modules?.length > 0 && (
                        <section className='news-modules'>
                            {page.modules.map((module, i, { length, ...modules }) => {
                                const isLast = length - 1 === i;
                                const nextModuleType = modules[i + 1]?.__typename;
                                const previousModuleType = modules[i - 1]?.__typename;
                                return (
                                    <ModuleDispatcher
                                        {...module}
                                        isLast={isLast}
                                        key={i + module?.id}
                                        nextModuleType={nextModuleType}
                                        pageTypename={page.__typename}
                                        previousModuleType={previousModuleType}/>
                                );
                            })}
                        </section>
                    )}
                </article>

            </main>

            {/* Footer */}
            {props.data?.footer && (
                <Footer {...props.data.footer} />
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            __typename: PropTypes.string,
            author: PropTypes.shape({
                title: PropTypes.string,
                url: PropTypes.string,
            }),
            customAuthorLabel: PropTypes.string,
            funds: PropTypes.shape({
                edges: PropTypes.arrayOf(PropTypes.shape({
                    node: PropTypes.shape({
                        title: PropTypes.string,
                        branding: PropTypes.string,
                        url: PropTypes.string,
                    }),
                }),),
            }),
            modules: PropTypes.array,
            publicationDate: PropTypes.string,
            subtitle: PropTypes.string,
            title: PropTypes.string.isRequired,
        }),
        navigation: PropTypes.object,
        footer: PropTypes.object,
    }),
    canonicalUrl: PropTypes.string.isRequired,
    domain: PropTypes.string,
};

NewsPage.propTypes = propTypes;

export default NewsPage;
